import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { webDtos as adminWebDtos } from '@vectronic/openApi-admin';
import { dsDtos, webDtos } from '@vectronic/openApi-customer';
import { DropOffDataTable, ParameterDataWithParameterAndSubsystem } from '@vectronic/util';
import { BehaviorSubject, combineLatest, filter, of, tap } from 'rxjs';
import { RemoteCommandProviderService } from '../remote-command-provider.service';

@Injectable({ providedIn: 'root' })
export class DropOffCommandService {
  private remoteCommandProviderService = inject(RemoteCommandProviderService, { optional: true });

  dropOffs$ = this.remoteCommandProviderService?.selectedDropOffs$ ?? of();
  dropOffs = toSignal(this.dropOffs$);
  remoteDeviceCommands = toSignal(this.remoteCommandProviderService?.remoteDeviceCommands$ ?? of());
  deviceTypes = toSignal(this.remoteCommandProviderService?.deviceTypes$ ?? of());

  selectedDropOffData$ = new BehaviorSubject<DropOffDataTable[]>([]);
  updatedDropOffData$ = new BehaviorSubject<DropOffDataTable[]>([]);

  selectedCollarIds$ = new BehaviorSubject<number[]>(undefined);
  refreshCollarsTrigger$ = new BehaviorSubject<void>(null);

  dropOffUpdater$ = combineLatest({
    parameters: this.remoteCommandProviderService?.parameters$ ?? of(),
    subsystems: this.remoteCommandProviderService?.parameterSubsystems$ ?? of(),
    collars: this.remoteCommandProviderService?.selectedCollars$ ?? of(),
    dropOffs: this.remoteCommandProviderService?.selectedDropOffs$ ?? of(),
    deviceTypes: this.remoteCommandProviderService?.deviceTypes$ ?? of(),
  }).pipe(
    filter(({ collars }) => !!collars?.length),
    tap(({ parameters, subsystems, collars, dropOffs, deviceTypes }) => {
      const data: DropOffDataTable[] = [];
      collars.forEach(
        ({
          internalDropOffId,
          id,
          lastReception,
          shares,
          externalDropOffId,
          internalDropOffRadioFiringParameterData,
          externalDropOffRadioFiringParameterData,
        }: webDtos.CollarExtended | adminWebDtos.CollarExtended) => {
          const baseData = {
            idDevice: id,
            animalName: this.remoteCommandProviderService?.getCurrentAnimalNameOfCollarId(id),
            deviceMetaInformation: { lastReception },
            admins: shares,
          } as Partial<DropOffDataTable>;

          const internalDropOff = dropOffs.find(({ id }) => id === internalDropOffId);
          const externalDropOff = dropOffs.find(({ id }) => id === externalDropOffId);

          if (internalDropOffId) {
            const deviceType = internalDropOff
              ? deviceTypes.find(({ id }) => id === internalDropOff.deviceTypeId)
              : undefined;
            const parameterList = internalDropOff
              ? this.mapParameterDataListToListWithParametersAndSubsystems(
                  internalDropOff.parameterDataList,
                  parameters,
                  subsystems,
                )
              : undefined;
            const dropOffParams = this.findDropOffParameters(parameterList);
            data.push({
              dropOffExtended: internalDropOff ? { ...internalDropOff, deviceType } : undefined,
              dropOffId: internalDropOffId,
              dropOffType: 'internal',
              ...baseData,
              ...(dropOffParams ?? {}),
              radioControl: dropOffParams?.radioControl ?? internalDropOffRadioFiringParameterData,
            } as DropOffDataTable);
          }
          if (externalDropOffId) {
            const deviceType = externalDropOff
              ? deviceTypes.find(({ id }) => id === externalDropOff.deviceTypeId)
              : undefined;
            const parameterList = externalDropOff
              ? this.mapParameterDataListToListWithParametersAndSubsystems(
                  externalDropOff.parameterDataList,
                  parameters,
                  subsystems,
                )
              : undefined;
            const dropOffParams = this.findDropOffParameters(parameterList);
            data.push({
              dropOffExtended: externalDropOff ? { ...externalDropOff, deviceType } : undefined,
              dropOffId: externalDropOffId,
              dropOffType: 'external',
              ...baseData,
              ...(dropOffParams ?? {}),
              radioControl: dropOffParams?.radioControl ?? externalDropOffRadioFiringParameterData,
            } as DropOffDataTable);
          }
        },
      );
      this.selectedDropOffData$.next(data.sort((a, b) => a.idDevice - b.idDevice));
    }),
  );

  get selectedCollarsIds() {
    const selectedDropOffData = this.selectedDropOffData$.value;
    return selectedDropOffData
      .map(({ idDevice }) => idDevice)
      .filter((value, index, array) => array.indexOf(value) === index);
  }

  remove(row: DropOffDataTable) {
    const dropOffData = this.selectedDropOffData$.value.filter(
      ({ idDevice, dropOffType }) => idDevice !== row.idDevice || dropOffType !== row.dropOffType,
    );
    this.selectedDropOffData$.next(dropOffData);
    this.updatedDropOffData$.next(dropOffData);
  }

  isEnabled() {
    const data = this.selectedDropOffData$.value;
    if (!data?.length) return false;
    const remoteDeviceCommands = this.remoteDeviceCommands();
    const hasDropOffRelease =
      !!remoteDeviceCommands?.DROP_OFF_RELEASE?.length ||
      !!remoteDeviceCommands?.DROP_OFF_RELEASE_LONG?.length;
    if (!hasDropOffRelease) return false;
    return data?.every(
      ({ admins, radioControl }) =>
        admins?.filter(({ role }) => role === 'DEVICE_MANAGER' || role === 'OWNER')?.length &&
        this.hasValidRadioControl(radioControl),
    );
  }

  devicesWithoutRadioControl() {
    const data = this.selectedDropOffData$.value;
    if (!data?.length) return [];
    return data
      ?.filter(({ radioControl }) => !this.hasValidRadioControl(radioControl))
      .map((dropOff) => dropOff.idDevice);
  }

  private hasValidRadioControl(radioControl: string): boolean {
    if (!radioControl) return false;
    const parsed = parseInt(radioControl, 10);
    return isNaN(parsed) ? true : parsed > 0;
  }

  devicesWithoutDeviceManagerPermission() {
    const data = this.selectedDropOffData$.value;
    if (!data?.length) return [];
    return data
      ?.filter(
        ({ admins }) =>
          !admins?.filter(({ role }) => role === 'DEVICE_MANAGER' || role === 'OWNER')?.length,
      )
      .map((dropOff) => dropOff.idDevice);
  }

  addDropOff(row: DropOffDataTable, dropOffId: string) {
    const deviceTypes = this.deviceTypes();
    const dropOff = this.dropOffs().find(({ id }) => id === Number(dropOffId));

    const data = this.selectedDropOffData$.value;
    const newRow = data.find(
      ({ idDevice, dropOffType }) => idDevice === row.idDevice && dropOffType === row.dropOffType,
    );
    if (!newRow) return;
    newRow.dropOffExtended = {
      ...dropOff,
      id: dropOff.id,
      deviceType: deviceTypes.find((t) => t.id == dropOff.deviceTypeId),
    };
    this.selectedDropOffData$.next(data);
    this.updatedDropOffData$.next(data);
  }

  private findParameter(
    list: ParameterDataWithParameterAndSubsystem[] | undefined,
    pId: number,
    sId: number,
  ) {
    return list?.find(
      ({ parameter }) => parameter.identifier === pId && parameter.subsystem.identifier === sId,
    )?.data;
  }

  private findDropOffParameters(list: ParameterDataWithParameterAndSubsystem[] | undefined) {
    return {
      radioControl: this.findParameter(list, 6, 16),
      relativeReleaseTime: this.findParameter(list, 5, 16),
      releaseTimeMode: this.findParameter(list, 3, 16),
      absoluteReleaseTime: this.findParameter(list, 4, 16),
    };
  }

  private mapParameterDataListToListWithParametersAndSubsystems(
    parameterDataList: dsDtos.ParameterData[] | undefined,
    parameters: dsDtos.Parameter[],
    subsystems: dsDtos.Subsystem[],
  ): ParameterDataWithParameterAndSubsystem[] | undefined {
    if (!parameterDataList?.length) return [];
    return parameterDataList.map((pd) => {
      const parameter = parameters.find((p) => p.id === pd.parameterId);
      const subsystem = subsystems.find((s) => s.id === parameter?.subsystemId);
      return {
        ...pd,
        parameter: { ...parameter, subsystem },
      } as ParameterDataWithParameterAndSubsystem;
    });
  }
}
